import { memo } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import LoyaltyProductLogo from 'loyalty/components/LoyaltyProductLogo'
import Badge from 'LuxKit/Badge'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import Clickable from 'components/Common/Clickable'
import focusStyle from 'styles/utils/focusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import Tooltip from 'LuxKit/Tooltip'

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 ${rem(4)};
  transition: background-color 0.2s;

  ${mediaQueryUp.tablet} {
    padding: 0 ${rem(8)};
  }
`

const LogoButton = styled(Clickable)`
  position: relative;
  white-space: nowrap;
  justify-content: center;
  text-decoration: none;
  flex-shrink: 0;
  touch-action: manipulation;
  cursor: pointer;
  user-select: none;
  background-image: none;
  align-items: center;
  text-align: center;

  display: flex;
  gap: ${rem(4)};
  height: ${rem(32)};

  ${mediaQueryUp.tablet} {
    height: ${rem(40)};
  }

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover > ${Logo} {
        background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
      }
    }

    &:focus-visible {
      ${focusStyle}
    }

    &:active > ${Logo} {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }
`

const NewBadge = styled(Badge)`
  position: absolute;
  top: ${rem(-8)};
  right: ${rem(-8)};
`

type LoyaltyVariant = 'luxPlus' | 'loyalty'

interface Props {
  isNew?: boolean
  variant: LoyaltyVariant
}

function NavBarLoyaltyLogo(props: Props) {
  const { variant, isNew } = props
  const intl = useIntl()

  const isLuxPlusMember = useAppSelector(state => getCanRedeemLuxPlusBenefits(state))

  const luxPlusTooltip = intl.formatMessage({
    id: '1FKc7eofOcl9GXxkkKDm',
    defaultMessage: 'Our premium membership: Unlock extra discounts, inclusions and hidden offers',
    description: 'Tooltip text for Lux Plus logo on the offer listing or detail page',
  })

  const loyaltyTooltip = intl.formatMessage({
    id: '2R23tjTNeGdKHikY3uAw',
    defaultMessage: 'Our free loyalty program: Earn points, upgrades and transfers as you travel',
    description: 'Tooltip text displayed when hovering over the loyalty program logo in the header',
  })

  const tooltipContent = variant === 'luxPlus' ? luxPlusTooltip : loyaltyTooltip

  const luxPlusJoinText = intl.formatMessage({
    id: 'riiInfd4OuMemvufgOfd',
    defaultMessage: 'Join our VIP Travel Club',
    description: 'Text shown for non-members to join Lux Plus for the membership',
  })

  const luxPlusMemberText = intl.formatMessage({
    id: 'fasgrSyV_71UXvEDjPuU',
    defaultMessage: 'VIP Travel Club',
    description: 'Text shown for Lux Plus members in the navigation bar',
  })

  const loyaltyText = intl.formatMessage({
    id: 'wC6SYCmvrT8Eou0DuUtt',
    defaultMessage: 'Earn points with',
    description: 'Text shown for loyalty program in the loyalty program section',
  })

  const newBadgeText = intl.formatMessage({
    id: 'CiWoHQLd_PEziL9FryAx',
    defaultMessage: 'New',
    description: 'Badge text indicating that a feature or item is newly available',
  })

  return <Tooltip description={tooltipContent} portaled>
    <LogoButton
      to={variant === 'luxPlus' ? '/lux-plus' : `/${LOYALTY_LANDING_PAGE}`}
    >
      <CSSBreakpoint min="desktop">
        <BodyText variant="small" colour="neutral-three">
          {variant === 'luxPlus' && (!isLuxPlusMember ? luxPlusJoinText : luxPlusMemberText)}
          {variant === 'loyalty' && loyaltyText}
        </BodyText>
      </CSSBreakpoint>
      <Logo>
        {variant === 'luxPlus' && <LuxPlusLogo height={12} />}
        {variant === 'loyalty' && <LoyaltyProductLogo height={19} />}
      </Logo>
      {isNew && <NewBadge mode="critical" kind="string">{newBadgeText}</NewBadge>}
    </LogoButton>
  </Tooltip>
}

export default memo(NavBarLoyaltyLogo)
